import React, { useState, useEffect, useRef } from 'react';
import ConfirmDialog from './ConfirmDialog';
import {
  Service,
  AdminStaff,
  BusinessHours,
  StaffInviteStatus,
  fetchAdminServices,
  fetchAdminStaff,
  createAdminStaff,
  updateAdminStaff,
  deleteAdminStaff,
  uploadStaffPhoto,
  resendStaffInvite,
  ApiError,
} from '../api';
import {
  Plus,
  Trash2,
  Check,
  Pencil,
  Loader2,
  Send,
  Upload,
  Users,
  AlertTriangle,
} from 'lucide-react';
import { TIME_OPTIONS, formatTime12 } from '../lib/time';

interface AdminStaffSettingsProps {
  /** The salon's hours — used to pre-fill a worker's custom schedule. */
  salonHours: BusinessHours;
}

const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const DAYS_SHORT: Record<string, string> = {
  Monday: 'Mon',
  Tuesday: 'Tue',
  Wednesday: 'Wed',
  Thursday: 'Thu',
  Friday: 'Fri',
  Saturday: 'Sat',
  Sunday: 'Sun',
};

function initials(name: string): string {
  return name
    .split(' ')
    .map((p) => p[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();
}

const INVITE_BADGE: Record<StaffInviteStatus, { label: string; className: string }> = {
  none: { label: 'No account', className: 'bg-muted text-fg-muted' },
  invited: { label: 'Invite sent', className: 'bg-warn-soft text-warn' },
  active: { label: 'Active', className: 'bg-success-soft text-success' },
};

interface StaffForm {
  name: string;
  email: string;
  serviceIds: number[];
  useCustomHours: boolean;
  hours: BusinessHours;
}

const SALON_DEFAULT_HOURS: BusinessHours = {
  Monday: { open: '09:00', close: '18:00' },
  Tuesday: { open: '09:00', close: '18:00' },
  Wednesday: { open: '09:00', close: '18:00' },
  Thursday: { open: '09:00', close: '18:00' },
  Friday: { open: '09:00', close: '18:00' },
  Saturday: null,
  Sunday: null,
};

export default function AdminStaffSettings({ salonHours }: AdminStaffSettingsProps) {
  const [staff, setStaff] = useState<AdminStaff[]>([]);
  const [services, setServices] = useState<Service[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState<StaffForm>({
    name: '',
    email: '',
    serviceIds: [],
    useCustomHours: false,
    hours: {},
  });
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState<{ id: number; name: string } | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [savedMessage, setSavedMessage] = useState('');
  const photoInputRef = useRef<HTMLInputElement>(null);

  const editingWorker = editingId !== null ? staff.find((w) => w.id === editingId) : null;
  // A worker can be added with no email at all — they're only on the schedule and can't
  // log in. An email can be added later to invite them; once they have an account the
  // address is theirs, so the field locks.
  const canSetEmail = editingId === null || editingWorker?.inviteStatus === 'none';

  useEffect(() => {
    load();
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const [s, w] = await Promise.all([fetchAdminServices(), fetchAdminStaff()]);
      setServices(s);
      setStaff(w);
    } catch {
      /* ignore */
    }
    setLoading(false);
  };

  const flashSaved = () => {
    setSavedMessage('Saved');
    setTimeout(() => setSavedMessage(''), 2500);
  };

  const openAdd = () => {
    setEditingId(null);
    setForm({
      name: '',
      email: '',
      serviceIds: services.map((s) => s.id),
      useCustomHours: false,
      hours: salonHours && Object.keys(salonHours).length ? salonHours : SALON_DEFAULT_HOURS,
    });
    setFormError('');
    setShowForm(true);
  };

  const openEdit = (worker: AdminStaff) => {
    const hasCustom = worker.businessHours && Object.keys(worker.businessHours).length > 0;
    setEditingId(worker.id);
    setForm({
      name: worker.name,
      email: worker.email,
      serviceIds: worker.serviceIds,
      useCustomHours: hasCustom,
      hours: hasCustom
        ? worker.businessHours
        : salonHours && Object.keys(salonHours).length
          ? salonHours
          : SALON_DEFAULT_HOURS,
    });
    setFormError('');
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingId(null);
  };

  const toggleService = (id: number) => {
    setForm((p) => ({
      ...p,
      serviceIds: p.serviceIds.includes(id)
        ? p.serviceIds.filter((x) => x !== id)
        : [...p.serviceIds, id],
    }));
  };

  const toggleDay = (day: string) => {
    setForm((p) => ({
      ...p,
      hours: { ...p.hours, [day]: p.hours[day] ? null : { open: '09:00', close: '18:00' } },
    }));
  };

  const updateDayTime = (day: string, field: 'open' | 'close', value: string) => {
    setForm((p) => ({
      ...p,
      hours: {
        ...p.hours,
        [day]: { ...(p.hours[day] as { open: string; close: string }), [field]: value },
      },
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setFormError('');
    const businessHours = form.useCustomHours ? form.hours : {};
    const email = form.email.trim();
    try {
      if (editingId === null) {
        await createAdminStaff({
          name: form.name,
          email: email || undefined,
          serviceIds: form.serviceIds,
          businessHours,
        });
      } else {
        await updateAdminStaff(editingId, {
          name: form.name,
          serviceIds: form.serviceIds,
          businessHours,
        });
        // Giving an account-less worker a login goes through the invite endpoint —
        // it owns account creation and the invite email; the PATCH above stays a
        // plain field update. It runs second because it is the irreversible half:
        // no account is provisioned unless the plain edits are known saved.
        if (canSetEmail && email) {
          try {
            await resendStaffInvite(editingId, email);
          } catch (err) {
            // The edits above are already saved, so say so — and keep the form open
            // with the typed email, since the row's resend button is hidden while
            // the worker has no email to resend to.
            await load();
            setFormError(
              err instanceof ApiError
                ? `Worker saved, but the invite couldn’t be sent: ${err.message}`
                : 'Worker saved, but the invite couldn’t be sent. Check the email and save again.',
            );
            setSaving(false);
            return;
          }
        }
      }
      await load();
      flashSaved();
      closeForm();
    } catch (err) {
      setFormError(err instanceof ApiError ? err.message : 'Could not save worker.');
    }
    setSaving(false);
  };

  const handleToggleActive = async (worker: AdminStaff) => {
    setBusyId(worker.id);
    try {
      await updateAdminStaff(worker.id, { isActive: !worker.isActive });
      await load();
    } catch {
      /* ignore */
    }
    setBusyId(null);
  };

  const handleResendInvite = async (worker: AdminStaff) => {
    setBusyId(worker.id);
    try {
      await resendStaffInvite(worker.id);
      await load();
      flashSaved();
    } catch {
      /* ignore */
    }
    setBusyId(null);
  };

  const handleDelete = async (id: number) => {
    try {
      await deleteAdminStaff(id);
      await load();
    } catch {
      /* ignore */
    }
    setDeleteConfirm(null);
  };

  const handleUploadPhoto = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || editingId === null) return;
    setSaving(true);
    try {
      await uploadStaffPhoto(editingId, file);
      await load();
    } catch {
      /* ignore */
    }
    setSaving(false);
    if (photoInputRef.current) photoInputRef.current.value = '';
  };

  // Once the salon has any active worker, scheduling is per-worker: a service with
  // no active worker assigned becomes unbookable (empty slots, booking rejected).
  // Surface those so the owner isn't silently caught out while assigning services
  // to workers one at a time.
  const activeWorkers = staff.filter((w) => w.isActive);
  const coveredServiceIds = new Set(activeWorkers.flatMap((w) => w.serviceIds));
  const uncoveredServices =
    activeWorkers.length > 0 ? services.filter((s) => !coveredServiceIds.has(s.id)) : [];

  return (
    <div className="space-y-6">
      {uncoveredServices.length > 0 && (
        <div className="border-warn/30 bg-warn-soft text-warn flex items-start gap-3 rounded-xl border p-4 text-sm">
          <AlertTriangle className="text-warn mt-0.5 h-5 w-5 shrink-0" />
          <div>
            <p className="font-semibold">
              {uncoveredServices.length === 1
                ? '1 service has no worker and can’t be booked'
                : `${uncoveredServices.length} services have no worker and can’t be booked`}
            </p>
            <p className="text-warn mt-0.5">
              Once a salon has staff, only services assigned to an active worker are bookable.
              Assign a worker to: {uncoveredServices.map((s) => s.name).join(', ')}.
            </p>
          </div>
        </div>
      )}
      <div className="border-default bg-surface overflow-hidden rounded-xl border shadow-sm">
        <div className="border-subtle flex items-center justify-between gap-3 border-b px-4 py-4 sm:px-6 sm:py-5">
          <div className="min-w-0">
            <h2 className="text-fg text-base font-bold sm:text-lg">Team Members</h2>
            <p className="text-fg-muted mt-0.5 hidden text-xs sm:block sm:text-sm">
              Add workers, choose their services and hours. Give a worker an email to send them
              their own login.
            </p>
          </div>
          <div className="flex shrink-0 items-center gap-2 sm:gap-3">
            {savedMessage && (
              <span className="text-success flex items-center gap-1 text-xs font-medium sm:text-sm">
                <Check className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                {savedMessage}
              </span>
            )}
            {!showForm && (
              <button
                onClick={openAdd}
                className="bg-brand-600 hover:bg-brand-700 flex items-center gap-1.5 rounded-lg px-3 py-2 text-xs font-medium text-white transition-colors sm:gap-2 sm:px-4 sm:text-sm"
              >
                <Plus className="h-4 w-4" />
                <span className="hidden sm:inline">Add Worker</span>
                <span className="sm:hidden">Add</span>
              </button>
            )}
          </div>
        </div>

        {/* ── Add / edit form ── */}
        {showForm && (
          <form
            onSubmit={handleSubmit}
            className="border-subtle bg-canvas/40 space-y-5 border-b px-4 py-5 sm:px-6"
          >
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <label className="text-fg-secondary mb-1 block text-xs font-semibold tracking-wide uppercase">
                  Name
                </label>
                <input
                  type="text"
                  required
                  value={form.name}
                  onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                  className="focus:ring-brand-600 border-default bg-surface w-full rounded-lg border px-3 py-2 text-sm outline-none focus:border-transparent focus:ring-2"
                  placeholder="Worker's name"
                />
              </div>
              <div>
                <label className="text-fg-secondary mb-1 block text-xs font-semibold tracking-wide uppercase">
                  Email (optional)
                </label>
                <input
                  type="email"
                  value={form.email}
                  disabled={!canSetEmail}
                  onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))}
                  className="focus:ring-brand-600 border-default bg-surface disabled:bg-muted disabled:text-fg-muted w-full rounded-lg border px-3 py-2 text-sm outline-none focus:border-transparent focus:ring-2"
                  placeholder="worker@email.com"
                />
                {canSetEmail && (
                  <p className="text-fg-muted mt-1 text-xs">
                    {editingId === null
                      ? 'Leave blank to add a worker who’s only on the schedule and can’t log in.'
                      : 'Add an email to send this worker a login invite.'}
                  </p>
                )}
              </div>
            </div>

            {/* Services */}
            <div>
              <label className="text-fg-secondary mb-1.5 block text-xs font-semibold tracking-wide uppercase">
                Services performed
              </label>
              {services.length === 0 ? (
                <p className="text-fg-subtle text-sm">Add services in the Salon tab first.</p>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {services.map((s) => {
                    const on = form.serviceIds.includes(s.id);
                    return (
                      <button
                        key={s.id}
                        type="button"
                        onClick={() => toggleService(s.id)}
                        className={`rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${
                          on
                            ? 'border-brand-600 bg-accent-soft text-accent'
                            : 'border-default bg-surface text-fg-muted hover:border-strong'
                        }`}
                      >
                        {on && <Check className="mr-1 inline h-3 w-3" />}
                        {s.name}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Hours */}
            <div>
              <label className="text-fg-secondary mb-1.5 block text-xs font-semibold tracking-wide uppercase">
                Working hours
              </label>
              <div className="mb-3 flex gap-2">
                <button
                  type="button"
                  onClick={() => setForm((p) => ({ ...p, useCustomHours: false }))}
                  className={`rounded-lg border px-3 py-1.5 text-xs font-medium transition-colors ${
                    !form.useCustomHours
                      ? 'border-brand-600 bg-accent-soft text-accent'
                      : 'border-default bg-surface text-fg-muted hover:border-strong'
                  }`}
                >
                  Same as salon
                </button>
                <button
                  type="button"
                  onClick={() => setForm((p) => ({ ...p, useCustomHours: true }))}
                  className={`rounded-lg border px-3 py-1.5 text-xs font-medium transition-colors ${
                    form.useCustomHours
                      ? 'border-brand-600 bg-accent-soft text-accent'
                      : 'border-default bg-surface text-fg-muted hover:border-strong'
                  }`}
                >
                  Custom hours
                </button>
              </div>

              {form.useCustomHours && (
                <div className="divide-subtle border-default bg-surface divide-y rounded-lg border">
                  {DAYS.map((day) => {
                    const dayHours = form.hours[day];
                    const isOpen = dayHours !== null && dayHours !== undefined;
                    return (
                      <div key={day} className="flex items-center gap-3 px-3 py-2.5 sm:gap-4">
                        <div className="text-fg w-10 text-sm font-medium sm:w-24">
                          <span className="sm:hidden">{DAYS_SHORT[day]}</span>
                          <span className="hidden sm:inline">{day}</span>
                        </div>
                        <button
                          type="button"
                          onClick={() => toggleDay(day)}
                          className={`relative h-5 w-10 shrink-0 rounded-full transition-colors ${
                            isOpen ? 'bg-brand-600' : 'bg-muted-strong'
                          }`}
                        >
                          <span
                            className={`bg-surface absolute top-0.5 left-0.5 h-4 w-4 rounded-full shadow transition-transform ${
                              isOpen ? 'translate-x-5' : 'translate-x-0'
                            }`}
                          />
                        </button>
                        {isOpen && dayHours ? (
                          <div className="flex min-w-0 items-center gap-1.5 text-sm">
                            <select
                              value={dayHours.open}
                              onChange={(e) => updateDayTime(day, 'open', e.target.value)}
                              className="focus:ring-brand-600 border-default bg-surface rounded-lg border px-1.5 py-1 text-xs outline-none focus:border-transparent focus:ring-2"
                            >
                              {TIME_OPTIONS.map((t) => (
                                <option key={t} value={t}>
                                  {formatTime12(t)}
                                </option>
                              ))}
                            </select>
                            <span className="text-fg-subtle text-xs">-</span>
                            <select
                              value={dayHours.close}
                              onChange={(e) => updateDayTime(day, 'close', e.target.value)}
                              className="focus:ring-brand-600 border-default bg-surface rounded-lg border px-1.5 py-1 text-xs outline-none focus:border-transparent focus:ring-2"
                            >
                              {TIME_OPTIONS.map((t) => (
                                <option key={t} value={t}>
                                  {formatTime12(t)}
                                </option>
                              ))}
                            </select>
                          </div>
                        ) : (
                          <span className="text-fg-subtle text-xs italic">Off</span>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Photo (edit only — needs a saved worker) */}
            {editingId !== null && (
              <div>
                <label className="text-fg-secondary mb-1.5 block text-xs font-semibold tracking-wide uppercase">
                  Photo
                </label>
                <div className="flex items-center gap-3">
                  {editingWorker?.photo ? (
                    <img
                      src={editingWorker.photo}
                      alt={form.name}
                      className="h-12 w-12 rounded-full object-cover"
                    />
                  ) : (
                    <div className="bg-accent-soft text-accent flex h-12 w-12 items-center justify-center rounded-full text-sm font-semibold">
                      {initials(form.name || '?')}
                    </div>
                  )}
                  <input
                    ref={photoInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleUploadPhoto}
                    className="hidden"
                  />
                  <button
                    type="button"
                    onClick={() => photoInputRef.current?.click()}
                    className="hover:text-accent border-default text-fg-secondary hover:border-strong flex items-center gap-2 rounded-lg border px-3 py-2 text-sm font-medium transition-colors"
                  >
                    <Upload className="h-4 w-4" />
                    Upload
                  </button>
                </div>
              </div>
            )}

            {formError && <p className="text-danger text-sm">{formError}</p>}

            <div className="flex items-center gap-2">
              <button
                type="submit"
                disabled={saving}
                className="bg-brand-600 hover:bg-brand-700 flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium text-white transition-colors disabled:opacity-50"
              >
                {saving ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Check className="h-4 w-4" />
                )}
                {editingId === null
                  ? form.email.trim()
                    ? 'Add worker & send invite'
                    : 'Add worker'
                  : canSetEmail && form.email.trim()
                    ? 'Save & send invite'
                    : 'Save changes'}
              </button>
              <button
                type="button"
                onClick={closeForm}
                className="bg-muted text-fg-secondary hover:bg-muted-strong rounded-lg px-4 py-2 text-sm font-medium transition-colors"
              >
                Cancel
              </button>
            </div>
          </form>
        )}

        {/* ── Worker list ── */}
        {loading ? (
          <div className="text-fg-subtle px-4 py-12 text-center sm:px-6">
            <Loader2 className="mx-auto mb-2 h-5 w-5 animate-spin" />
            Loading workers...
          </div>
        ) : staff.length === 0 ? (
          <div className="text-fg-subtle px-4 py-12 text-center sm:px-6">
            <Users className="mx-auto mb-2 h-8 w-8" />
            <p className="text-sm">
              No workers yet. Your salon is booked at the salon level until you add one.
            </p>
          </div>
        ) : (
          <div className="divide-subtle divide-y">
            {staff.map((worker) => {
              const badge = INVITE_BADGE[worker.inviteStatus];
              const serviceLabel =
                worker.serviceIds.length === services.length
                  ? 'All services'
                  : `${worker.serviceIds.length} service${worker.serviceIds.length === 1 ? '' : 's'}`;
              const customHours =
                worker.businessHours && Object.keys(worker.businessHours).length > 0;
              return (
                <div
                  key={worker.id}
                  className={`hover:bg-canvas/50 flex items-center gap-3 px-4 py-3 transition-colors sm:gap-4 sm:px-6 sm:py-4 ${
                    worker.isActive ? '' : 'opacity-60'
                  }`}
                >
                  {worker.photo ? (
                    <img
                      src={worker.photo}
                      alt={worker.name}
                      className="h-10 w-10 shrink-0 rounded-full object-cover"
                    />
                  ) : (
                    <div className="bg-accent-soft text-accent flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-sm font-semibold">
                      {initials(worker.name)}
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-fg truncate text-sm font-medium sm:text-base">
                        {worker.name}
                      </span>
                      <span
                        className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${badge.className}`}
                      >
                        {badge.label}
                      </span>
                    </div>
                    <div className="text-fg-muted mt-0.5 truncate text-xs">
                      {/* No email is a valid state, not a gap — the "No account"
                          badge already says so, so drop the segment entirely. */}
                      {[worker.email, serviceLabel, customHours ? 'Custom hours' : 'Salon hours']
                        .filter(Boolean)
                        .join(' · ')}
                    </div>
                  </div>

                  <div className="flex shrink-0 items-center gap-1">
                    {worker.inviteStatus !== 'active' && worker.email && (
                      <button
                        onClick={() => handleResendInvite(worker)}
                        disabled={busyId === worker.id}
                        className="hover:text-accent text-fg-subtle hover:bg-canvas rounded-md p-1.5 transition-colors disabled:opacity-50"
                        title="Resend invite"
                      >
                        <Send className="h-3.5 w-3.5" />
                      </button>
                    )}
                    <button
                      onClick={() => handleToggleActive(worker)}
                      disabled={busyId === worker.id}
                      className={`rounded-full px-2 py-1 text-[11px] font-medium transition-colors disabled:opacity-50 ${
                        worker.isActive
                          ? 'bg-success-soft text-success hover:bg-success-soft'
                          : 'bg-muted text-fg-muted hover:bg-muted-strong'
                      }`}
                      title={worker.isActive ? 'Deactivate (hide from booking)' : 'Activate'}
                    >
                      {worker.isActive ? 'Active' : 'Inactive'}
                    </button>
                    <button
                      onClick={() => openEdit(worker)}
                      className="hover:text-accent text-fg-subtle hover:bg-canvas rounded-md p-1.5 transition-colors"
                      title="Edit worker"
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </button>
                    <button
                      onClick={() => setDeleteConfirm({ id: worker.id, name: worker.name })}
                      className="text-fg-subtle hover:bg-danger-soft hover:text-danger rounded-md p-1.5 transition-colors"
                      title="Delete worker"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <ConfirmDialog
        open={deleteConfirm !== null}
        title="Delete Worker"
        message={`Remove "${deleteConfirm?.name}"? Their past appointments are kept but unassigned. This cannot be undone.`}
        confirmLabel="Delete"
        variant="danger"
        onConfirm={() => deleteConfirm && handleDelete(deleteConfirm.id)}
        onCancel={() => setDeleteConfirm(null)}
      />
    </div>
  );
}
